import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import Page from 'components/Page';
import PageRow from 'components/PageRow';

import { useRVerifyPin } from '@repositories/common';
import useGlobalState from 'actions';

import { inputPinContainer, inputPinPage } from './styles';
import type { InputPinProps } from './types';
import PinComponent from './components/pin';
import { ER30, PR30 } from '@julofinance/color-token';
import { Typography } from '@julo-ui/react';
import NewSubHeader from 'components/NewSubHeader';
import { useActionLog } from '@hooks/use-action-log';

const PIN_COUNT = 6;

const InputPin = ({
  withHeader = true,
  onClose,
  onSubmit,
  errorMessage,
  isVerify,
  page,
  subHeaderTitle = 'PIN',
}: InputPinProps) => {
  const [pinValue, setPinValue] = useState('');
  const history = useHistory();
  const [, actions] = useGlobalState();
  const [pinErrorMessage, setPinErrorMessage] = useState<{
    title: string;
    subtitle?: string;
  } | null>(errorMessage || null);
  const [verifyErrorMessage, setVerifyErrorMessage] = useState('');
  const maxPinWarning = useMemo(
    () => pinErrorMessage?.subtitle || false,
    [pinErrorMessage],
  );

  const { pushEvent } = useActionLog({ page: 'input_pin_page' });

  const verifyPinMutation = useRVerifyPin({
    onSuccess: () => {
      pushEvent({
        element:
          page == 'loan_offer'
            ? 'success_input_pin_loan_offer'
            : 'success_input_pin',
        event: 'action',
      });
      onSubmit(pinValue);
    },
    onError: (err) => {
      pushEvent({
        element:
          page == 'loan_offer'
            ? 'failed_input_pin_loan_offer'
            : 'failed_input_pin',
        event: 'action',
      });
      const errorMessage =
        ((err.payload?.errors || []) as Array<string>)[0] || '';
      if (errorMessage.includes('User has no pin')) {
        actions.setState('isNoPinDialogOpen', true);
      } else {
        setVerifyErrorMessage(errorMessage || 'Sedang terjadi masalah');
      }
    },
    onSettled: () => {
      actions.closeLoadingOverlay();
    },
  });

  const handleInputPin = async (value: string) => {
    setPinValue(value);

    if (value.length === PIN_COUNT) {
      if (isVerify) {
        actions.openLoadingOverlay();
        await verifyPinMutation.mutateAsync({
          variables: {
            pin: value,
          },
        });
      } else {
        onSubmit(value);
      }
    }
  };

  const renderErrorMessage = useMemo(() => {
    // display bottom sheet instead of error message below input pin for max attempt
    if (maxPinWarning) return null;

    if (!!pinErrorMessage?.title) {
      return (
        <>
          <Typography
            type='body'
            color={ER30}
            size='small'
            sx={{ textAlign: 'center', marginTop: '16px' }}
          >
            {pinErrorMessage?.title}
          </Typography>
          {!!pinErrorMessage?.subtitle && (
            <Typography
              type='body'
              color={ER30}
              size='small'
              sx={{ textAlign: 'center', marginTop: '16px' }}
            >
              {pinErrorMessage?.subtitle}
            </Typography>
          )}
        </>
      );
    }

    if (verifyErrorMessage) {
      return (
        <Typography
          type='body'
          color={ER30}
          size='small'
          sx={{ textAlign: 'center', marginTop: '16px' }}
        >
          {pinErrorMessage?.title || verifyErrorMessage}
        </Typography>
      );
    }

    return null;
  }, [
    pinErrorMessage?.subtitle,
    pinErrorMessage?.title,
    maxPinWarning,
    verifyErrorMessage,
  ]);

  useEffect(() => {
    if (errorMessage?.title !== pinErrorMessage?.title) {
      setPinValue('');
      setPinErrorMessage(errorMessage || null);
    }
  }, [errorMessage, pinErrorMessage?.title]);

  return (
    <>
      <Helmet>
        <title>JULO - Input PIN</title>
      </Helmet>
      <Page useHeader={withHeader} className={inputPinPage}>
        <NewSubHeader title={subHeaderTitle} onClickBack={onClose} />
        <PageRow padding='16px' className='pageRow' firstLevelContainer>
          <div className={inputPinContainer}>
            <h2 className='heading'>Ketik PIN</h2>
            <PinComponent
              value={pinValue}
              onChange={handleInputPin}
              error={!!verifyErrorMessage || !!errorMessage?.title}
            />
            {renderErrorMessage}
            <Typography
              type='body'
              color={PR30}
              bold
              size='small'
              sx={{ textAlign: 'center', marginTop: '42px', cursor: 'pointer' }}
              onClick={() => history.push('/forgot_pin')}
            >
              Lupa PIN?
            </Typography>
          </div>
        </PageRow>
      </Page>
    </>
  );
};

export default InputPin;
